import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  Label,
} from "recharts";
import { DOTCHARTCOLORS } from "../../utils/constants";

type DotChartProps = {
  data: any[] | undefined;
  vendors: string[] | undefined | null;
  baseSku?: string;
  date?: string;
  isShowDotValue?: boolean;
};

const BASE_VENDOR_COLORS: Record<string, string> = {
  "ALAN WIRE": "#a0dbfe",
  CERROWIRE: "#e97132",
  "CME WIRE": "#da291c",
  "COLONIAL WIRE": "#007cb0",
  ENCORE: "#50b539",
  "REPUBLIC WIRE": "#9c765f",
  "SERVICE WIRE": "#041e42",
  SOUTHWIRE: "#a02b93",
  OMNICABLE: "#203c74",
  "PRIORITY WIRE": "#ffc000",
  "Other Suppliers": "#bcbcbc",
  ATKORE: "#62b5e5",
  ZEKELMAN: "#012169",
  NUCOR: "#046A38",
  IPEX: "#9c765f",
  "PRIME CONDUIT": "#8cd17e",
  CANTEX: "#0097a9",
  "NATIONAL PIPE": "#FF921D",
  "SOUTHERN PIPE": "#b17aa1",
};

const currency = (v: number) => `$${v.toFixed(2)}`;

const DotWithLabel = ({
  cx,
  cy,
  value,
  isValueVisible,
  color,
  onDotClick,
}: {
  cx?: number;
  cy?: number;
  value?: number;
  isValueVisible?: boolean;
  color: string;
  onDotClick?: (e: any) => void;
}) => {
  // Hide dot when value is null/undefined or not a finite number (zero still shows)
  const n = Number(value);
  if (value == null || !Number.isFinite(n)) return null;
  return (
    <g
      onClick={(e) => {
        e.stopPropagation();
        onDotClick?.(e);
      }}
    >
      <circle cx={cx} cy={cy} r={5} fill={color} stroke={color} />
      {isValueVisible && (
        <text
          x={cx}
          y={(cy ?? 0) - 12}
          textAnchor="middle"
          fill="#374151"
          fontSize={11}
        >
          {currency(n)}
        </text>
      )}
    </g>
  );
};

function buildYAxisTicks(
  data: any[] | undefined,
  vendors: string[] | undefined | null,
): number[] | undefined {
  if (!data || !vendors || vendors.length === 0) return undefined;

  const values: number[] = [];
  for (const entry of data) {
    for (const vendor of vendors) {
      const key = vendor.toUpperCase(); // matches Line dataKey
      const val = Number(entry?.[key]);
      if (Number.isFinite(val) && val !== 0) {
        values.push(val);
      }
    }
  }

  if (values.length === 0) return undefined;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const low = Math.floor(min);
  const high = Math.ceil(max);

  const span = Math.max(1, high - low);
  const step = Math.max(1, Math.round(span / 4));

  return [low, low + step, low + step * 2, low + step * 3, high + 2];
}

export default function DotChart({
  data,
  vendors,
  baseSku,
  date,
  isShowDotValue,
}: DotChartProps) {
  const [suppressTooltip, setSuppressTooltip] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  const [ticks, setTicks] = useState<number[] | undefined>(undefined);

  const colorsObj = useMemo(() => {
    const map: Record<string, string> = {};
    // add uppercase and lowercase keys
    Object.entries(BASE_VENDOR_COLORS).forEach(([name, color]) => {
      map[name] = color; // original (often UPPERCASE or spaced)
      map[name.toUpperCase()] = color; // uppercase
      map[name.toLowerCase()] = color; // lowercase
    });
    return map;
  }, [vendors]);

  // Tooltip that hides when suppress is true; shows all vendors for the active district otherwise
  const CustomTooltip = (props: any) => {
    const { active, label, payload, suppress } = props;
    if (!active || suppress) return null;
    return (
      <div className="bg-white border border-gray-300 rounded px-3 py-2 text-[12px]">
        <div className="mb-1">{label}</div>
        {payload
          ?.sort((a: any, b: any) => Number(b.value) - Number(a.value))
          .map((p: any, idx: number) => {
            let colorCode = p.name.toLowerCase();
            return (
              <div key={p} className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: colorsObj[colorCode] }}
                />
                {p.name.charAt(0).toUpperCase() + p.name.slice(1)} : ${p.value}
              </div>
            );
          })}
      </div>
    );
  };

  useEffect(() => {
    setTicks(buildYAxisTicks(data, vendors));
  }, [data, vendors]);

  // Hide tooltip when clicking anywhere outside the chart area
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (chartRef.current && !chartRef.current.contains(e.target as Node)) {
        setSuppressTooltip(true);
      }
    };
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, []);

  return (
    <div className="flex gap-3">
      {data?.length !== 0 ? (
        <div className="w-full flex flex-col" ref={chartRef}>
          <div className="font-bold pt-[1.2vh] pb-[0.5vh] text-[1.9vh]">
            {baseSku} Supplier Quotes for {date}
          </div>
          <div className="flex text-[14px] gap-5 flex-wrap">
            {vendors?.map((v: string, i: number) => (
              <div key={v} className="flex text-xs items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: colorsObj[v] }}
                />
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </div>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={data}
              margin={{ top: 20, right: 40, bottom: 15, left: 5 }}
              onMouseEnter={() => setSuppressTooltip(false)} // hover inside chart -> enable tooltip
            >
              <CartesianGrid
                horizontal={false}
                vertical={false}
                strokeDasharray="3 3"
              />
              <XAxis
                dataKey="district"
                padding={{ left: 50, right: 15 }}
                tickMargin={15}
              />
              <YAxis
                padding={{ top: 5 }}
                domain={([DataMin, DataMax]) => [DataMin - 2, DataMax]}
                ticks={ticks}
                tickFormatter={(value) => `$${value}`}
                tick={true}
                tickMargin={0}
              >
                <Label
                  className="text-fs16"
                  style={{
                    textAnchor: "middle",
                    fill: "black",
                  }}
                  angle={-90}
                  value={"\n" + `${baseSku} Quote ($/cft)`}
                  position={"insideLeft"}
                />
              </YAxis>

              <Tooltip
                trigger="hover"
                cursor={
                  suppressTooltip
                    ? false
                    : { stroke: "#9CA3AF", strokeDasharray: "4 4" }
                }
                content={<CustomTooltip suppress={suppressTooltip} />}
              />
              {vendors?.map((v: string, i: number) => (
                <Line
                  key={v}
                  name={v.charAt(0).toUpperCase() + v.slice(1)}
                  type="monotone"
                  dataKey={v.toUpperCase()}
                  stroke="transparent"
                  dot={
                    <DotWithLabel
                      color={colorsObj[v]}
                      isValueVisible={isShowDotValue}
                    />
                  }
                  activeDot={{ r: 6 }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="w-full flex flex-col">
          {" "}
          <div className="font-bold pt-[1.2vh] pb-[0.5vh] text-[1.9vh] w-full flex justify-center items-center h-[10vh]">
            Dot chart not displayed, as no supplier quotes were received for{" "}
            {date}
          </div>
        </div>
      )}
    </div>
  );
}
